import { toast } from "@overmap-ai/blocks"
import { APIError } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback, useEffect } from "react"
import { useMap } from "react-map-gl/mapbox"

import { Slideshow, type SlideshowResult } from "../../components/Slideshow/Slideshow"
import { WORLD_LNG_LAT_BOUNDS } from "../../mapbox/constants"
import { sdk } from "../../sdk"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/settings/projects/create")

export const CreateProjectView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const { current: map } = useMap()
	const navigate = useNavigate()

	useEffect(() => {
		if (!map) return
		map?.fitBounds(WORLD_LNG_LAT_BOUNDS, { animate: false })
		// REASON: Only want to reset the map view on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onSubmitCallback = useCallback(
		async ({ projectName, bounds }: SlideshowResult) => {
			try {
				const project = await sdk.projects.add({
					name: projectName,
					bounds: bounds,
					organization: organizationId,
				})

				void navigate({
					from: "/$organizationId/settings/projects/create",
					to: "/$organizationId/settings/projects/$projectId",
					params: (prev) => ({
						...prev,
						projectId: project.uuid,
					}),
				})

				toast.success({
					title: `Project created`,
					description: `The project ${projectName} was successfully created.`,
				})
			} catch (error) {
				toast.error({
					title: "Something went wrong.",
					description: error instanceof APIError ? error.message : "An unexpected error occurred.",
				})
			}
		},
		[organizationId, navigate],
	)

	return (
		<div className="pointer-events-none absolute inset-0 flex flex-col">
			<div className="relative grow">
				<Slideshow onSubmitCallback={onSubmitCallback} />
			</div>
		</div>
	)
})

CreateProjectView.displayName = "CreateProjectView"
